import React, { useState, useEffect, useRef } from 'react';
import { AdminLayout, ActionDropdown, ConfirmModal } from '../../components';
import api from '../../services/api';
import { formatDate, toInputDate } from '../../utils/dateUtils';
import SEO from '../../components/common/SEO';

const SubscriptionManagement = () => {
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, active: 0, expired: 0, cancelled: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [showModal, setShowModal] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [formData, setFormData] = useState({
    studentId: '', endDate: '', planType: 'monthly', paymentMethod: 'manual', paymentReference: '', notes: '', status: 'active',
  });
  const [saving, setSaving] = useState(false);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const hasFetchedRef = useRef(false);

  // Enrollment requests state
  const [enrollRequests, setEnrollRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestFilter, setRequestFilter] = useState('all');
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approvingRequest, setApprovingRequest] = useState(null);
  const [approveForm, setApproveForm] = useState({
    paymentReference: '', planType: 'monthly', endDate: '', paymentMethod: 'manual', notes: '',
  });
  const [confirm, setConfirm] = useState({ open: false, title: '', message: '', variant: 'danger', icon: 'warning', confirmText: '', onConfirm: null });

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchSubscriptions();
      fetchStats();
      fetchStudents();
      fetchCourses();
      fetchEnrollmentRequests();
      hasFetchedRef.current = true;
    }
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(search && { search }),
      };
      const data = await api.getSubscriptions(params);
      setSubscriptions(data.subscriptions || []);
      setPagination(prev => ({ ...prev, total: data.pagination?.total || 0, pages: data.pagination?.pages || 1 }));
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await api.getSubscriptionStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchStudents = async () => {
    try {
      const data = await api.getUsers({ role: 'student', limit: '1000' });
      setStudents(data.users || []);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const data = await api.getCourses({ limit: '100' });
      setCourses(data.courses || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchEnrollmentRequests = async () => {
    setRequestsLoading(true);
    try {
      const params = {};
      if (requestFilter !== 'all') params.status = requestFilter;
      const data = await api.getEnrollmentRequests(params);
      setEnrollRequests(data.requests || []);
    } catch (error) {
      console.error('Error fetching enrollment requests:', error);
    } finally {
      setRequestsLoading(false);
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current) fetchSubscriptions();
  }, [search, statusFilter]);

  useEffect(() => {
    if (hasFetchedRef.current) fetchEnrollmentRequests();
  }, [requestFilter]);

  const calculateEndDate = (planType) => {
    const date = new Date();
    switch (planType) {
      case 'monthly': date.setMonth(date.getMonth() + 1); break;
      case 'quarterly': date.setMonth(date.getMonth() + 3); break;
      case 'yearly': date.setFullYear(date.getFullYear() + 1); break;
      case 'lifetime': date.setFullYear(date.getFullYear() + 100); break;
      default: date.setMonth(date.getMonth() + 1);
    }
    return toInputDate(date);
  };

  const handleQuickDatePreset = (months) => {
    const date = new Date();
    date.setMonth(date.getMonth() + months);
    setFormData(prev => ({ ...prev, endDate: toInputDate(date) }));
  };

  const handlePlanTypeChange = (planType) => {
    setFormData(prev => ({ ...prev, planType, endDate: calculateEndDate(planType) }));
  };

  const handleOpenModal = (subscription = null) => {
    if (subscription) {
      setEditingSubscription(subscription);
      let allowedCourseIds = [];
      if (subscription.allowedCourses && Array.isArray(subscription.allowedCourses)) {
        allowedCourseIds = subscription.allowedCourses.map(c => {
          if (typeof c === 'object' && c !== null) return (c._id || c.id || c).toString();
          return c ? c.toString() : null;
        }).filter(Boolean);
      }
      setSelectedCourses(allowedCourseIds);
      setFormData({
        studentId: subscription.student._id,
        endDate: toInputDate(subscription.endDate),
        planType: subscription.planType,
        paymentMethod: subscription.paymentMethod,
        paymentReference: subscription.paymentReference || '',
        notes: subscription.notes || '',
        status: subscription.status || 'active',
      });
    } else {
      setEditingSubscription(null);
      setSelectedCourses([]);
      setFormData({
        studentId: '', endDate: calculateEndDate('monthly'), planType: 'monthly',
        paymentMethod: 'manual', paymentReference: '', notes: '', status: 'active',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingSubscription(null);
    setSelectedCourses([]);
    setFormData({
      studentId: '', endDate: calculateEndDate('monthly'), planType: 'monthly',
      paymentMethod: 'manual', paymentReference: '', notes: '', status: 'active',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const coursesToSend = selectedCourses.map(id => id.toString()).filter(Boolean);
      if (editingSubscription) {
        await api.updateSubscription(editingSubscription._id, {
          endDate: formData.endDate, planType: formData.planType, paymentMethod: formData.paymentMethod,
          paymentReference: formData.paymentReference, notes: formData.notes, status: formData.status,
          allowedCourses: coursesToSend,
        });
      } else {
        await api.createSubscription({
          studentId: formData.studentId, endDate: formData.endDate, planType: formData.planType,
          paymentMethod: formData.paymentMethod, paymentReference: formData.paymentReference,
          notes: formData.notes, allowedCourses: coursesToSend,
        });
      }
      handleCloseModal();
      setTimeout(() => { fetchSubscriptions(); fetchStats(); }, 100);
    } catch (error) {
      alert(error.message || 'Failed to save subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (sub) => {
    setConfirm({
      open: true,
      title: 'Cancel Subscription?',
      message: `This will cancel ${sub.student?.name || 'this student'}'s subscription. They will lose access to their courses.`,
      variant: 'warning',
      icon: 'warning',
      confirmText: 'Cancel Subscription',
      onConfirm: async () => {
        try {
          await api.updateSubscription(sub._id, { status: 'cancelled' });
          fetchSubscriptions();
          fetchStats();
        } catch (error) {
          alert(error.message || 'Failed to cancel subscription');
        }
        setConfirm(prev => ({ ...prev, open: false }));
      },
    });
  };

  const handleDelete = (sub) => {
    setConfirm({
      open: true,
      title: 'Delete Subscription?',
      message: `This will permanently delete ${sub.student?.name || 'this student'}'s subscription record. This action cannot be undone.`,
      variant: 'danger',
      icon: 'delete',
      confirmText: 'Delete Permanently',
      onConfirm: async () => {
        try {
          await api.deleteSubscription(sub._id);
          fetchSubscriptions();
          fetchStats();
        } catch (error) {
          alert(error.message || 'Failed to delete subscription');
        }
        setConfirm(prev => ({ ...prev, open: false }));
      },
    });
  };

  // Enrollment request handlers
  const handleApproveRequest = (request) => {
    setApprovingRequest(request);
    setApproveForm({
      paymentReference: '', planType: 'monthly',
      endDate: calculateEndDate('monthly'), paymentMethod: 'manual', notes: '',
    });
    setShowApproveModal(true);
  };

  const handleApproveSubmit = async (e) => {
    e.preventDefault();
    if (!approveForm.paymentReference) {
      alert('Payment reference is required');
      return;
    }
    setSaving(true);
    try {
      await api.approveEnrollmentRequest(approvingRequest._id, approveForm);
      setShowApproveModal(false);
      setApprovingRequest(null);
      fetchEnrollmentRequests();
      fetchSubscriptions();
      fetchStats();
      alert('Enrollment request approved successfully!');
    } catch (error) {
      alert(error.message || 'Failed to approve request');
    } finally {
      setSaving(false);
    }
  };

  const handleRejectRequest = (req) => {
    setConfirm({
      open: true,
      title: 'Reject Enrollment Request?',
      message: `This will reject ${req.name}'s enrollment request for "${req.course?.title || 'this course'}". The student will be notified.`,
      variant: 'danger',
      icon: 'reject',
      confirmText: 'Reject Request',
      onConfirm: async () => {
        try {
          await api.rejectEnrollmentRequest(req._id);
          fetchEnrollmentRequests();
        } catch (error) {
          alert(error.message || 'Failed to reject request');
        }
        setConfirm(prev => ({ ...prev, open: false }));
      },
    });
  };

  const isSubscriptionActive = (sub) => sub.status === 'active' && new Date(sub.endDate) > new Date();

  const pendingRequestsCount = enrollRequests.filter(r => r.status === 'pending').length;

  return (
    <AdminLayout>
      <SEO title="Subscription Management" noIndex />
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-dark-900 mb-2">Subscription Management</h1>
          <p className="text-dark-500">Manage student subscriptions and enrollment requests</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="card p-4">
            <p className="text-sm text-dark-500 mb-1">Total Subscriptions</p>
            <p className="text-2xl font-bold text-dark-900">{stats.total}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-dark-500 mb-1">Active</p>
            <p className="text-2xl font-bold text-green-600">{stats.active}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-dark-500 mb-1">Expired</p>
            <p className="text-2xl font-bold text-orange-600">{stats.expired}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-dark-500 mb-1">Cancelled</p>
            <p className="text-2xl font-bold text-red-600">{stats.cancelled}</p>
          </div>
          <div className="card p-4">
            <p className="text-sm text-dark-500 mb-1">Pending Requests</p>
            <p className="text-2xl font-bold text-blue-600">{pendingRequestsCount}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-dark-200">
          <button
            onClick={() => setActiveTab('subscriptions')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${activeTab === 'subscriptions' ? 'border-primary-500 text-primary-600' : 'border-transparent text-dark-500 hover:text-dark-700'}`}
          >
            Subscriptions
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'requests' ? 'border-primary-500 text-primary-600' : 'border-transparent text-dark-500 hover:text-dark-700'}`}
          >
            Enrollment Requests
            {pendingRequestsCount > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingRequestsCount}</span>
            )}
          </button>
        </div>

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <>
            <div className="card p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                <div className="flex flex-col md:flex-row gap-4 flex-1 w-full md:w-auto">
                  <input type="text" placeholder="Search by student name or email..." value={search} onChange={(e) => setSearch(e.target.value)} className="px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 flex-1" />
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="expired">Expired</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <button onClick={() => handleOpenModal()} className="btn-primary px-6 py-2 whitespace-nowrap">+ Add Subscription</button>
              </div>
            </div>

            <div className="card overflow-hidden min-h-[320px]">
              {loading ? (
                <div className="p-8 text-center text-dark-500">Loading subscriptions...</div>
              ) : subscriptions.length === 0 ? (
                <div className="p-8 text-center text-dark-500">No subscriptions found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-50 border-b border-dark-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-dark-700 uppercase">Student</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-dark-700 uppercase">Plan</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-dark-700 uppercase">End Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-dark-700 uppercase">Payment</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-dark-700 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-dark-700 uppercase w-16">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-dark-200">
                      {subscriptions.map((sub) => (
                        <tr key={sub._id} className="hover:bg-dark-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-semibold mr-3">
                                {sub.student?.name?.charAt(0).toUpperCase() || 'S'}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-dark-900">{sub.student?.name || 'Unknown'}</div>
                                <div className="text-sm text-dark-500">{sub.student?.email || ''}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-600 capitalize">{sub.planType}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-600">{formatDate(sub.endDate)}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-600">
                            <div className="flex flex-col">
                              <span className="capitalize">{sub.paymentMethod?.replace('_', ' ')}</span>
                              {sub.paymentReference ? (
                                <span className="text-xs text-dark-400 mt-1">Ref: {sub.paymentReference.substring(0, 15)}{sub.paymentReference.length > 15 ? '...' : ''}</span>
                              ) : (
                                <span className="text-xs text-red-500 mt-1">No reference</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${isSubscriptionActive(sub) ? 'bg-green-100 text-green-800' : sub.status === 'cancelled' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}`}>
                              {isSubscriptionActive(sub) ? 'Active' : sub.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <ActionDropdown actions={[
                              {
                                label: 'Edit Subscription',
                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>,
                                onClick: () => handleOpenModal(sub),
                              },
                              {
                                label: 'Cancel Subscription',
                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>,
                                variant: 'warning',
                                onClick: () => handleCancel(sub),
                                hidden: sub.status !== 'active',
                                divider: true,
                              },
                              {
                                label: 'Delete',
                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>,
                                variant: 'danger',
                                onClick: () => handleDelete(sub),
                                divider: sub.status !== 'active',
                              },
                            ]} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Enrollment Requests Tab */}
        {activeTab === 'requests' && (
          <>
            <div className="card p-4 mb-6">
              <div className="flex gap-4 items-center">
                <select value={requestFilter} onChange={(e) => setRequestFilter(e.target.value)} className="px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>

            <div className="card overflow-hidden min-h-[320px]">
              {requestsLoading ? (
                <div className="p-8 text-center text-dark-500">Loading requests...</div>
              ) : enrollRequests.length === 0 ? (
                <div className="p-8 text-center text-dark-500">No enrollment requests found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-50 border-b border-dark-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-dark-700 uppercase">Student</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-dark-700 uppercase">Contact</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-dark-700 uppercase">Course</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-dark-700 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-dark-700 uppercase">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-dark-700 uppercase w-16">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-dark-200">
                      {enrollRequests.map((req) => (
                        <tr key={req._id} className="hover:bg-dark-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-dark-900">{req.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-dark-600">{req.email}</div>
                            <div className="text-sm text-dark-500">{req.phone}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-600">
                            {req.course?.title || 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-dark-600">
                            {formatDate(req.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              req.status === 'approved' ? 'bg-green-100 text-green-800' :
                              req.status === 'rejected' ? 'bg-red-100 text-red-800' :
                              req.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {req.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <ActionDropdown actions={[
                              {
                                label: 'Approve & Activate',
                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                                variant: 'success',
                                onClick: () => handleApproveRequest(req),
                                hidden: req.status !== 'pending' && req.status !== 'processing',
                              },
                              {
                                label: 'Reject Request',
                                icon: <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" /></svg>,
                                variant: 'danger',
                                onClick: () => handleRejectRequest(req),
                                hidden: req.status !== 'pending' && req.status !== 'processing',
                                divider: true,
                              },
                            ]} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {/* Add/Edit Subscription Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-dark-900 mb-4">
                {editingSubscription ? 'Edit Subscription' : 'Add Subscription'}
              </h2>
              {!editingSubscription && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>After Payment:</strong> Select student, choose plan type, enter payment reference (required), and save.
                  </p>
                </div>
              )}
              <form onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">Student</label>
                    <select value={formData.studentId} onChange={(e) => setFormData(prev => ({ ...prev, studentId: e.target.value }))} required disabled={!!editingSubscription} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                      <option value="">Select a student</option>
                      {students.map((s) => <option key={s._id} value={s._id}>{s.name} ({s.email})</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">End Date</label>
                    <div className="flex gap-2 mb-2">
                      {[{m: 1, l: '1 Month'}, {m: 3, l: '3 Months'}, {m: 6, l: '6 Months'}, {m: 12, l: '1 Year'}].map(({m, l}) => (
                        <button key={m} type="button" onClick={() => handleQuickDatePreset(m)} className="px-3 py-1 text-xs bg-dark-100 hover:bg-dark-200 rounded text-dark-700">{l}</button>
                      ))}
                    </div>
                    <input type="date" value={formData.endDate} onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))} required className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">Plan Type</label>
                    <select value={formData.planType} onChange={(e) => handlePlanTypeChange(e.target.value)} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                      <option value="monthly">Monthly (1 month)</option>
                      <option value="quarterly">Quarterly (3 months)</option>
                      <option value="yearly">Yearly (12 months)</option>
                      <option value="lifetime">Lifetime</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">Payment Method</label>
                    <select value={formData.paymentMethod} onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                      <option value="manual">Manual</option>
                      <option value="scanner">Scanner</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">Payment Reference <span className="text-red-500">*</span></label>
                    <input type="text" value={formData.paymentReference} onChange={(e) => setFormData(prev => ({ ...prev, paymentReference: e.target.value }))} placeholder="Transaction ID, Receipt Number..." required className="w-full px-4 py-2 border-2 border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">Notes</label>
                    <textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))} rows={3} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">Allowed Courses <span className="text-xs text-dark-500 ml-2 font-normal">(Leave empty for all)</span></label>
                    <div className="max-h-48 overflow-y-auto border border-dark-200 rounded-lg p-2 bg-dark-50">
                      {courses.map((course) => {
                        const courseId = course._id.toString();
                        const isSelected = selectedCourses.includes(courseId);
                        return (
                          <label key={course._id} className="flex items-center gap-2 p-2 hover:bg-white rounded cursor-pointer">
                            <input type="checkbox" checked={isSelected} onChange={(e) => {
                              if (e.target.checked) setSelectedCourses(prev => [...prev, courseId]);
                              else setSelectedCourses(prev => prev.filter(id => id !== courseId));
                            }} className="rounded border-dark-300 text-primary-500 focus:ring-primary-500" />
                            <span className="text-sm text-dark-700">{course.title}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                  {editingSubscription && (
                    <div>
                      <label className="block text-sm font-medium text-dark-700 mb-1">Status</label>
                      <select value={formData.status} onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                        <option value="active">Active</option>
                        <option value="pending">Pending</option>
                        <option value="expired">Expired</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  )}
                </div>
                <div className="flex gap-4 mt-6">
                  <button type="button" onClick={handleCloseModal} className="flex-1 px-4 py-2 border border-dark-200 rounded-lg hover:bg-dark-50">Cancel</button>
                  <button type="submit" disabled={saving} className="flex-1 btn-primary px-4 py-2 disabled:opacity-50">{saving ? 'Saving...' : editingSubscription ? 'Update' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Approve Request Modal */}
        {showApproveModal && approvingRequest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-dark-900 mb-2">Approve Enrollment</h2>
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-800">
                  <strong>{approvingRequest.name}</strong> requested enrollment in <strong>{approvingRequest.course?.title}</strong>
                </p>
                <p className="text-xs text-green-700 mt-1">Email: {approvingRequest.email} | Phone: {approvingRequest.phone}</p>
              </div>
              <form onSubmit={handleApproveSubmit}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">Payment Reference <span className="text-red-500">*</span></label>
                    <input type="text" value={approveForm.paymentReference} onChange={(e) => setApproveForm(prev => ({ ...prev, paymentReference: e.target.value }))} required placeholder="Transaction ID, Receipt Number..." className="w-full px-4 py-2 border-2 border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">Plan Type</label>
                    <select value={approveForm.planType} onChange={(e) => setApproveForm(prev => ({ ...prev, planType: e.target.value, endDate: calculateEndDate(e.target.value) }))} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                      <option value="lifetime">Lifetime</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">End Date</label>
                    <input type="date" value={approveForm.endDate} onChange={(e) => setApproveForm(prev => ({ ...prev, endDate: e.target.value }))} required className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">Payment Method</label>
                    <select value={approveForm.paymentMethod} onChange={(e) => setApproveForm(prev => ({ ...prev, paymentMethod: e.target.value }))} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500">
                      <option value="manual">Manual</option>
                      <option value="scanner">Scanner</option>
                      <option value="bank_transfer">Bank Transfer</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-dark-700 mb-1">Notes</label>
                    <textarea value={approveForm.notes} onChange={(e) => setApproveForm(prev => ({ ...prev, notes: e.target.value }))} rows={2} className="w-full px-4 py-2 border border-dark-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500" />
                  </div>
                </div>
                <div className="flex gap-4 mt-6">
                  <button type="button" onClick={() => { setShowApproveModal(false); setApprovingRequest(null); }} className="flex-1 px-4 py-2 border border-dark-200 rounded-lg hover:bg-dark-50">Cancel</button>
                  <button type="submit" disabled={saving} className="flex-1 btn-primary px-4 py-2 disabled:opacity-50">{saving ? 'Approving...' : 'Approve & Activate'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
        <ConfirmModal
          open={confirm.open}
          onClose={() => setConfirm(prev => ({ ...prev, open: false }))}
          onConfirm={confirm.onConfirm}
          title={confirm.title}
          message={confirm.message}
          confirmText={confirm.confirmText}
          variant={confirm.variant}
          icon={confirm.icon}
        />
      </div>
    </AdminLayout>
  );
};

export default SubscriptionManagement;
